import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow, parseISO } from 'date-fns';
import type { EpicSummary } from '../../../../shared/types/epics';
import { StateIndicator } from '../shared/StateIndicator';
import { StoryProgressBar } from '../shared/StoryProgressBar';
import { ArchiveControl } from './ArchiveControl';
import {
  LANES,
  NEEDS_HUMAN_STATES,
  placementForState,
  projectColor,
  shortRepo,
  type LaneId,
} from './boardLanes';

interface EpicBoardProps {
  epics: EpicSummary[];
  isLoading: boolean;
  error: Error | null;
  onCardClick: (epicRef: string) => void;
}

function relative(iso: string): string {
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

/** Project identity for a card: the repo, or the anchor repo (+N) for cross-repo epics. */
function projectKey(e: EpicSummary): string {
  return e.repo;
}

export const ProjectChip: React.FC<{ epic: EpicSummary }> = ({ epic }) => {
  const color = projectColor(projectKey(epic));
  const extra = epic.involvedRepos && epic.involvedRepos.length > 1 ? epic.involvedRepos.length - 1 : 0;
  const title = epic.involvedRepos && epic.involvedRepos.length > 0 ? epic.involvedRepos.join(', ') : epic.repo;
  return (
    <span
      data-testid="project-chip"
      title={title}
      className={`inline-flex items-center gap-1 max-w-full text-2xs font-medium rounded border px-1.5 py-0.5 ${color.chip}`}
    >
      <span className="truncate">{shortRepo(epic.repo)}</span>
      {extra > 0 && <span className="shrink-0 opacity-70">+{extra}</span>}
    </span>
  );
};

export const EpicCard: React.FC<{ epic: EpicSummary; onClick: (ref: string) => void; withArchive?: boolean }> = ({
  epic,
  onClick,
  withArchive,
}) => {
  const color = projectColor(projectKey(epic));
  const needsYou = NEEDS_HUMAN_STATES.has(epic.state);
  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={`epic-card-${epic.id}`}
      aria-label={`${epic.kind === 'bugfix' ? 'Bug fix' : 'Epic'} ${epic.number}: ${epic.title}`}
      onClick={() => onClick(epic.epicRef)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(epic.epicRef);
        }
      }}
      className="relative bg-white rounded-lg border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300
                 cursor-pointer overflow-hidden focus-visible:ring-2 focus-visible:ring-primary-300 outline-none transition-shadow"
    >
      <span aria-hidden className={`absolute left-0 top-0 bottom-0 w-1 ${color.bar}`} />
      <div className="pl-3 pr-2.5 py-2.5 space-y-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <ProjectChip epic={epic} />
          {epic.epicKind === 'migration' && (
            <span className="text-2xs rounded px-1 py-0.5 bg-slate-100 text-slate-600">migration</span>
          )}
          <span className="ml-auto flex items-center gap-1.5 shrink-0 text-2xs text-slate-500 font-mono">
            {epic.kind === 'bugfix' && (
              <span aria-label="Bug fix" title="Bug fix">🔧</span>
            )}
            #{epic.number}
          </span>
        </div>

        <p className="text-sm text-slate-900 leading-snug line-clamp-3" title={epic.title}>
          {epic.title}
        </p>

        <div className="flex items-center gap-2 flex-wrap">
          <StateIndicator state={epic.state} />
          {needsYou && (
            <span
              data-testid="needs-you"
              className="text-2xs font-semibold rounded px-1.5 py-0.5 bg-amber-100 text-amber-800"
            >
              Needs you
            </span>
          )}
          {epic.prUrl && (
            <a
              href={epic.prUrl}
              target="_blank"
              rel="noreferrer"
              title="Open pull request"
              onClick={(e) => e.stopPropagation()}
              className="text-2xs font-medium rounded px-1.5 py-0.5 bg-primary-50 text-primary-700 border border-primary-100 hover:bg-primary-100"
            >
              PR
            </a>
          )}
          {epic.archive?.hidden && (
            <span className="text-2xs rounded px-1.5 py-0.5 bg-slate-100 text-slate-600">
              {epic.archive.archived ? 'Archived' : 'Auto-hidden'}
            </span>
          )}
        </div>

        {epic.storyCount > 0 && (
          <StoryProgressBar completed={epic.completedStoryCount} total={epic.storyCount} />
        )}

        <div className="flex items-center justify-between gap-2">
          <span className="text-2xs text-slate-500">{relative(epic.updatedAt)}</span>
          {withArchive && (
            <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
              <ArchiveControl epic={epic} />
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const Skeleton: React.FC = () => (
  <div data-testid="epic-board-skeleton" aria-live="polite" aria-label="Loading epics" className="flex gap-3">
    {LANES.map((l) => (
      <div key={l.id} className="w-72 shrink-0 rounded-xl bg-slate-100 p-3 space-y-2">
        <div className="h-4 w-24 bg-slate-200 rounded animate-pulse" />
        <div className="h-20 bg-slate-200 rounded animate-pulse" />
        <div className="h-20 bg-slate-200 rounded animate-pulse" />
      </div>
    ))}
  </div>
);

/** Trello-style board: one column per lane, cards sorted by most recently updated. */
export const EpicBoard: React.FC<EpicBoardProps> = ({ epics, isLoading, error, onCardClick }) => {
  const byLane = useMemo(() => {
    const by: Record<LaneId, EpicSummary[]> = {
      requirements: [], planning: [], building: [], deploy: [], review: [], done: [], blocked: [],
    };
    for (const e of epics) {
      const p = placementForState(e.state);
      if (p === 'backlog') continue;
      by[p].push(e);
    }
    const newestFirst = (a: EpicSummary, b: EpicSummary) => b.updatedAt.localeCompare(a.updatedAt);
    (Object.keys(by) as LaneId[]).forEach((k) => by[k].sort(newestFirst));
    return by;
  }, [epics]);

  if (isLoading) return <Skeleton />;
  if (error) {
    return (
      <div role="alert" className="text-danger-700 bg-danger-50 px-5 py-4 text-sm rounded-xl">
        Failed to load epics. {error.message}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div data-testid="epic-board" className="flex gap-3 overflow-x-auto pb-2 items-start">
        {LANES.map((lane) => {
          const cards = byLane[lane.id];
          return (
            <section
              key={lane.id}
              data-testid={`lane-${lane.id}`}
              aria-label={`${lane.label} (${cards.length})`}
              className="w-72 shrink-0 rounded-xl bg-slate-100 border border-slate-200 flex flex-col max-h-[calc(100vh-16rem)]"
            >
              <div className={`h-1 rounded-t-xl ${lane.accent}`} />
              <header className="px-3 pt-2.5 pb-2 flex items-center justify-between" title={lane.hint}>
                <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-700">{lane.label}</h2>
                <span
                  data-testid={`lane-${lane.id}-count`}
                  className="text-2xs font-mono tabular-nums bg-white text-slate-600 rounded-full px-2 py-0.5 border border-slate-200"
                >
                  {cards.length}
                </span>
              </header>
              <div className="px-2 pb-2 space-y-2 overflow-y-auto">
                {cards.length === 0 ? (
                  <div className="text-2xs text-slate-400 text-center py-6">Nothing here</div>
                ) : (
                  cards.map((e) => (
                    <EpicCard key={e.epicRef} epic={e} onClick={onCardClick} withArchive={lane.id === 'blocked'} />
                  ))
                )}
                {lane.id === 'requirements' && (
                  <Link
                    to="/epics/new"
                    data-testid="board-new-epic"
                    className="block rounded-lg border-2 border-dashed border-slate-300 text-slate-500 hover:text-primary-700
                               hover:border-primary-300 hover:bg-white text-sm text-center py-3 transition-colors"
                  >
                    + Create new epic
                  </Link>
                )}
              </div>
            </section>
          );
        })}
      </div>

    </div>
  );
};
